<template lang="pug">
    el-card.contains-table( shadow="always" )
        .error(v-if="error")
            template(v-if="error.code")
                Error(:message="$t(error.message || 'Something went wrong!')" :code="error.code")
            template(v-else)
                Error(:message="$t('Something went wrong!')" :code="501")
        template(v-else)
            paginated-items(
                :scope="scope"
                :type="type"
                :loading="loading"
                :items="itemsPaginated.items"
                :filters="filters"
                :show-filters="showFilters"
                :variables="itemsPaginated.variables"
                :all-columns="allColumns"
                :active-columns="activeColumns"
                :page-info="itemsPaginated.pageInfo"
                :sortables="sortables"
                :sort="sort"
                @pagination-size-change="handleSizeChange"
                @pagination-current-change="handleCurrentChange"
                @show-table-columns="handleColsShow"
                @change-filter="handleFilterInputChange"
                @click="handleClick"
                @change-sort="handleSortInputChange"
            )
                template(
                    v-for="(_, name) in $scopedSlots"
                    v-if="name !== 'top'"
                    v-slot:[name]="scope"
                )
                    slot(:name="name" v-bind="scope")
                template(slot="top")
                    el-row.table-top(v-loading="loading" type="flex" justify="space-between")
                        .row
                            .left
                                el-button(
                                    v-if="showAddButton"
                                    type="primary"
                                    size="mini"
                                    icon="el-icon-fa-plus fas"
                                    @click="addItem"
                                )
                                    | {{ $t('Create new') }}
                            .right.hide-on-empty
                                el-button(
                                    plain
                                    size="mini"
                                    type="primary"
                                    icon="el-icon-fa-filter fas"
                                    @click="toggleFilterVisiblity"
                                    :disabled="disable.showFilters || hasFilters"
                                )
                                    span(v-if="showFilters") {{ $t('Hide filters') }}
                                    span(v-else) {{ $t('Show filters') }}

</template>

<i18n>
    {
        "de": {
            "Create new": "Neu anlegen",
            "Show filters": "Filter anzeigen",
            "Hide filters": "Filter verbergen",
            "List could not be fetched.": "Liste konnte nicht abgerufen werden."
        }
    }
</i18n>

<script>
import _ from 'lodash';

import { adminClient as apolloClient } from '@/store/apollo';
import router from '@/router';
import pinia from '@/pinia';
import { useSetupStore } from '@/stores';

import PaginatedItems from '@/components/PaginatedItems';
import Error from '@/components/Error'

export default {
    name: 'PaginatedItemsWrapper',
    props: {
        itemsPaginated: {
            type: Object,
            required: true
        },
        routerPath: {
            type: String,
            required: false
        },
        loading: {
            type: Boolean,
            default: false
        },
        sortables: {
            type: Array,
            default: () => []
        },
        scope: {
            type: String,
            default: 'default'
        },
        type: {
            type: String,
            default: 'default'
        },
        showAddButton: {
            type: Boolean,
            default: true
        }
    },
    data() {
        return {
            error: false,
            disable: {
                showFilters: false,
            },
        };
    },
    components: {
        PaginatedItems,
        Error,
    },
    watch: {
        activeCms(cms) {
            const reload = _.get(cms, 'reloadComponents', true);
            if (reload) this.initialize();
        }
    },
    computed: {
        showFilters() {
            return this.hasFilters || _.get(this.itemsPaginated, 'showFilters') || false;
        },
        hasFilters() {
            return !_.isEmpty(this.filters);
        },
        filters() {
            let filters = _.cloneDeep(this.itemsPaginated.filters);
            delete filters.cms;
            return filters;
        },
        sort() {
            var variables = this.itemsPaginated.variables;
            var defaultSort = this.itemsPaginated.defaultSort || { field: 'id', direction: 'desc' };
            var order = (variables.order !== undefined && variables.order !== '') ? variables.order : defaultSort.field;
            var direction = (variables.direction !== undefined && variables.direction !== '') ? variables.direction : defaultSort.direction;

            return { field: order, direction: direction };
        },
        allColumns() {
            let allColumns = this.itemsPaginated.allColumns;

            if (!this.enabledExtensions.includes('shop')) {
                allColumns = allColumns.filter(el => el.value_path.startsWith('customer.') === false);
            }

            return allColumns;
        },
        activeColumns() {
            let activeColumns = this.itemsPaginated.activeColumns;

            if (!this.enabledExtensions.includes('shop')) {
                activeColumns = activeColumns.filter(el => el.value_path.startsWith('customer.') === false);
            }

            return activeColumns;
        },
        setupStore() {
            return useSetupStore();
        },
        activeCms() {
            return this.setupStore.activeCms;
        },
        enabledExtensions() {
            return this.setupStore.enabledExtensions;
        }
    },
    methods: {
        defineActiveColumns(visibleColumns) {
            this.$emit('defineActiveColumns', visibleColumns);
        },
        initialize() {
            this.$emit("updateLoading", true)
            this.refetch();
        },
        addItem() {
            const params = { id: 'new' };
            router.push({ name: this.routerPath, params });
        },
        handleClick(item) {
            const params = { id: item.id };
            router.push({ name: this.routerPath, params });
        },
        handleSizeChange(itemsPerPage) {
            this.$emit("updateLoading", true)
            this.$emit("defineItemsPerPage", itemsPerPage)
            this.refetch();
        },
        handleCurrentChange(currentPage) {
            this.$emit("updateLoading", true)
            this.$emit("defineCurrentPage", currentPage)
            this.refetch();
        },
        handleColsShow(visibleColumns) {
            this.defineActiveColumns(visibleColumns);
            apolloClient.clearStore();
        },
        handleFilterInputChange(key, value, debounce = true) {
            this.$emit("defineCurrentPage", 0)
            this.$emit("defineFilterValue", { key, value })
            if (debounce) this.debouncedRefetch();
            else this.refetch();
        },
        handleSortInputChange(key, value) {
            this.$emit("defineSort", { key, value })
            this.refetch();
        },
        toggleFilterVisiblity() {
            const visible = this.showFilters;

            let value = false;
            if (!visible) value = true;

            this.disable.showFilters = true;
            this.$emit("defineFilterVisibility", value)
            this.saveFilterVisibility({ scope: this.scope, key: this.type, value }).finally(
                () => (this.disable.showFilters = false)
            );
        },
        refetch() {
            this.$emit("updateLoading", true)
            const cmsID = this.activeCms.id;
            const filter = {
                key: 'cms',
                match: cmsID
            };

            this.$emit("defineFilterValue", { key: 'cms', value: filter })
            this.$emit("updateLoading", true)
            this.$emit('fetchItems', this.activeCms.shop.id);
        },
        debouncedRefetch: _.debounce(function () {
            this.$emit("updateLoading", true)
            this.refetch();
        }, useSetupStore(pinia).debounce || 300),
        saveFilterVisibility(payload) {
            return this.setupStore.saveFilterVisibility(payload);
        },
        activateTab(item) {
            return this.setupStore.activateTab(item);
        },
    },
    mounted() {
        this.$emit("updateLoading", true)
        this.initialize();
    }
};
</script>

<style lang="sass" scoped>
.table-top
    display: block
    .row
        display: flex
        justify-content: space-between

.error
    padding: 20px
</style>
